import { useState, type ReactNode } from 'react'
import { ArrowRight, FileText, LayoutGrid, Repeat } from 'lucide-react'
import type { Filtre } from '../../../lib/groupes/sessions'
import { GroupTransfer } from './GroupTransfer'
import { ListingsModule } from './groupes/ListingsModule'
import { SousGroupesModule } from './groupes/SousGroupesModule'

type Vue = 'hub' | 'listings' | 'sousgroupes' | 'transfert'

interface GroupsManagementProps {
  onNavigatePage?: (page: string) => void
}

function Tuile({
  titre,
  texte,
  icone,
  teinte,
  onClick,
}: {
  titre: string
  texte: string
  icone: ReactNode
  teinte: { fond: string; survol: string; fleche: string }
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`group relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-left hover:shadow-md ${teinte.survol} transition-all duration-200`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${teinte.fond}`}>{icone}</div>
        <div className="pr-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{titre}</h3>
          <p className="text-sm text-gray-500">{texte}</p>
        </div>
      </div>
      <ArrowRight className={`absolute top-8 right-6 h-5 w-5 text-gray-300 ${teinte.fleche} transition-colors`} />
    </button>
  )
}

/**
 * L'entrée « Groupes » : trois modules séparés. Les listings ne font que lire ;
 * les sous-groupes et le transfert modifient les inscriptions.
 */
export function GroupsManagement({ onNavigatePage }: GroupsManagementProps) {
  const [vue, setVue] = useState<Vue>('hub')
  // Depuis les sous-groupes, « Imprimer les listes » ouvre les listings sur la même semaine.
  const [filtreListings, setFiltreListings] = useState<Partial<Filtre> | undefined>(undefined)
  const retour = () => setVue('hub')

  if (vue === 'listings') {
    return <ListingsModule onBack={retour} filtreInitial={filtreListings} />
  }

  if (vue === 'sousgroupes') {
    return (
      <SousGroupesModule
        onBack={retour}
        onImprimer={(filtre) => {
          setFiltreListings(filtre)
          setVue('listings')
        }}
      />
    )
  }

  if (vue === 'transfert') {
    return (
      <GroupTransfer
        onBack={retour}
        onNavigateDashboard={onNavigatePage ? () => onNavigatePage('dashboard') : undefined}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gestion des groupes</h1>
        <p className="text-gray-600">Listes à imprimer, sous-groupes et transferts d’enfants</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Tuile
          titre="Listings"
          texte="Présences, fiches santé (inclusion et droit à l’image compris) et natation, par session ou pour toute une semaine."
          icone={<FileText className="h-6 w-6 text-emerald-600" />}
          teinte={{ fond: 'bg-emerald-50 group-hover:bg-emerald-100', survol: 'hover:border-emerald-300', fleche: 'group-hover:text-emerald-500' }}
          onClick={() => {
            setFiltreListings(undefined)
            setVue('listings')
          }}
        />
        <Tuile
          titre="Sous-groupes"
          texte="Partager une session en petits et grands, ou en groupes de couleur, puis ajuster enfant par enfant."
          icone={<LayoutGrid className="h-6 w-6 text-blue-600" />}
          teinte={{ fond: 'bg-blue-50 group-hover:bg-blue-100', survol: 'hover:border-blue-300', fleche: 'group-hover:text-blue-500' }}
          onClick={() => setVue('sousgroupes')}
        />
        <Tuile
          titre="Transfert de groupe"
          texte="Transférer un enfant d’une session vers une autre session disponible."
          icone={<Repeat className="h-6 w-6 text-amber-600" />}
          teinte={{ fond: 'bg-amber-50 group-hover:bg-amber-100', survol: 'hover:border-amber-300', fleche: 'group-hover:text-amber-500' }}
          onClick={() => setVue('transfert')}
        />
      </div>
    </div>
  )
}
